import React, { Fragment } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Card, CardBody, CardHeader, Col, Container, Row, Label, Input, Spinner } from 'reactstrap';
import BreadCrumb from '../components/Common/BreadCrumb';

import { createDeepClone } from "../utils/index";

import CardLoader from "../components/CardLoader";
import ActionList from "../components/ActionList";
import ServiceDocumentGrid from "../components/ServiceDocumentGrid";

import { useDispatch, useSelector } from "react-redux";
import * as testingScopeActions from "../actions/IneTestingScopeActions";

const IneTestingScopeEditScreen  = (props) => {
    const { register, clearErrors, reset, trigger, getValues, formState: { errors } } = useForm({mode: 'all'});

    const navigate = useNavigate();
    const { id } = useParams();

    const dispatch = useDispatch();

    const loading = useSelector((state) => state.scope.loadingFBO);
    const fbo = useSelector((state) => state.scope.fbo);
    
    const [fboState, setFBOState] = React.useState(fbo);
    const [isDirty, setIsDirty] = React.useState(false);
    const [refreshAction, setRefreshAction] = React.useState(false);

    React.useEffect(() => {
	    dispatch(testingScopeActions.loadTestingScope(id, navigate));
    }, [id]);

    React.useEffect(() => {
		setFBOState(fbo);
        setIsDirty(false);
        setRefreshAction(!refreshAction);
        
        if(fbo) {
            reset(fbo.testingScope);
        }
        
        clearErrors();
    }, [fbo]);

    const reloadAction = () => {
        setRefreshAction(!refreshAction);
    }

    const onChangeDocument = (isProduct) => {
        setRefreshAction(!refreshAction);
    }

    const onTestingScopeChange = (name, value, isBlur) => {
        var copy_fboState = createDeepClone(fboState);

        if(value && isBlur) {
            value = value.trim();
        }

        if(value) {
            copy_fboState.testingScope[name] = value;
        }
        else if(value === false) {
            copy_fboState.testingScope[name] = false;
        }
        else {
            copy_fboState.testingScope[name] = null;
        }
        
        setFBOState(copy_fboState);
        setIsDirty(true);
    }

    const onTestingScopeDetailAdd = () => {
        var copy_fboState = createDeepClone(fboState);
        var copy_testingScopeDetail = createDeepClone(fboState.testingScopeDetail);
        
        copy_fboState.testingScope.testingScopeDetailList.push(copy_testingScopeDetail);
        
        setFBOState(copy_fboState);
        setIsDirty(true);
    }

    const onTestingScopeDetailChange = (index, name, value, isBlur) => {
        var copy_fboState = createDeepClone(fboState);

        if(value && isBlur) {
            value = value.trim();
        }

        if(value) {
            copy_fboState.testingScope.testingScopeDetailList[index][name] = value;
        }
        else if(value === false) {
            copy_fboState.testingScope.testingScopeDetailList[index][name] = false;
        }
        else {
            copy_fboState.testingScope.testingScopeDetailList[index][name] = null;
        }

        setFBOState(copy_fboState);
        setIsDirty(true);
    }

    const onTestingScopeDetailRemove = (index) => {
        var copy_fboState = createDeepClone(fboState);
        
        copy_fboState.testingScope.testingScopeDetailList.splice(index, 1);
        
        setFBOState(copy_fboState);
        setIsDirty(true);
    }

    const onSaveTestingScope = async() => {
        const valid = await trigger();
        if(valid) {
            dispatch(testingScopeActions.saveTestingScope(fboState, navigate));
        }
    }

    const onCancelTestingScope = () => {
        setFBOState(fbo);
        setIsDirty(false);
        clearErrors();
    }
    
    return(
        <Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title={fboState && fboState.testingScope.id > 0 ? "Edit Scope" : "Add Scope"} />
                    <Row>
                        <Col lg={fboState && fboState.testingScope.id > 0 ? 9 : 12}>
                            <CardLoader loading={loading} />

                            <Card className="mb-0">
                                <CardHeader className="border-0 d-flex align-items-center">
                                    <h5 className="card-title mb-0 flex-grow-1">Scope</h5>
                                    <div className="flex-shrink-0">
                                        <div className="d-flex flex-wrap gap-2">
                                            <button className="btn btn-primary btn-label"
                                                disabled={loading || !isDirty}
                                                onClick={onSaveTestingScope}
                                            >
                                                <i className="ri-save-line label-icon align-middle"></i> Save Scope
                                            </button>
                                            <button className="btn btn-danger btn-label"
                                                disabled={loading || !isDirty}
                                                onClick={onCancelTestingScope}
                                            >
                                                <i className="ri-close-line label-icon align-middle"></i> Cancel
                                            </button>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardBody className="border border-dashed border-end-0 border-start-0">
                                    <Row className="gy-2">
                                        <Col xxl={8} md={8}>
                                            <Label className="form-label">Name<span className="required">*</span></Label>
                                            <input type="text" className={`form-control ${errors.name ? "is-invalid" : ""}`}
                                                title={errors.name?.message}
                                                value={(fboState && fboState.testingScope.name) || ""}
                                                {...register("name", { required: "Name is required" })}
                                                onChange={e => onTestingScopeChange("name", e.target.value, false)}
                                            />
                                        </Col>
                                        <Col xxl={4} md={4}>
                                            <Label className="form-label">Active</Label>
                                            <select className="form-select"
                                                value={(fboState && fboState.testingScope.active) || false}
                                                onChange={e => onTestingScopeChange("active", e.target.value == "true" ? true : false)}
                                            >
                                                <option value={true}>Yes</option>
                                                <option value={false}>No</option>
                                            </select>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                            {fboState && fboState.testingScope.id > 0 &&
                            <Card>
                                <CardHeader className="border-0 d-flex align-items-center">
                                    <h5 className="card-title mb-0 flex-grow-1">Sub Scopes</h5>
                                    <div className="flex-shrink-0">
                                        <div className="d-flex flex-wrap gap-2">
                                            <button className="btn btn-primary btn-label"
                                                onClick={() => onTestingScopeDetailAdd()}
                                            >
                                                <i className="ri-add-line label-icon align-bottom"></i> Add Sub Scope
                                            </button>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardBody className="border border-dashed border-end-0 border-start-0">
                                    <Row className="gy-2">
                                        <Col xxl={6} md={6}>
                                            <Label className="form-label">Name</Label>
                                        </Col>
                                        <Col xxl={1} md={1}>
                                            <Label className="form-label">Order</Label>
                                        </Col>
                                        <Col xxl={2} md={2}>
                                            <Label className="form-label">Perform Inspection</Label>
                                        </Col>
                                        <Col xxl={2} md={2}>
                                            <Label className="form-label">Active</Label>
                                        </Col>
                                        <Col xxl={1} md={1}>
                                            <Label className="form-label">&nbsp;</Label>
                                        </Col>
                                        {fboState && fboState.testingScope && fboState.testingScope.testingScopeDetailList && fboState.testingScope.testingScopeDetailList.filter(x => x.isDeleted === false).length === 0 && <Col xxl={12} md={12}><Label className="form-label">No sub scopes added.</Label></Col>}
                                        {fboState && fboState.testingScope && fboState.testingScope.testingScopeDetailList && fboState.testingScope.testingScopeDetailList.map(function(item, index){
                                            return (
                                                <Row className={`gy-1 ${item.isDeleted ? "d-none" : ""}`} key={index}>
                                                    <Col xxl={6} md={6}>
                                                        <input type="text" maxLength={256} className={`form-control ${errors.items?.[index]?.name ? "is-invalid" : ""}`} placeholder="sub scope name..."
                                                            title={errors.items?.[index]?.name?.message}
                                                            value={item.name || ""}
                                                            {...register(`items.${index}.name`, { required: "Sub Scope Name is required" })}
                                                            onChange={e => onTestingScopeDetailChange(index, "name", e.target.value, false)}
                                                        />
                                                    </Col>
                                                    <Col xxl={1} md={1}>
                                                        <input type="text" className="form-control" placeholder="order..."
                                                            value={item.displayOrder || 0}
                                                            onChange={e => onTestingScopeDetailChange(index, "displayOrder", e.target.value, false)}
                                                        />
                                                    </Col>
                                                    <Col xxl={2} md={2}>
                                                        <select className="form-select"
                                                            value={(item.performInspection) || false}
                                                            onChange={e => onTestingScopeDetailChange(index, "performInspection", e.target.value == "true" ? true : false, false)}
                                                        >
                                                            <option value={true}>Yes</option>
                                                            <option value={false}>No</option>
                                                        </select>
                                                    </Col>
                                                    <Col xxl={2} md={2}>
                                                        <select className="form-select"
                                                            value={(item.active) || false}
                                                            onChange={e => onTestingScopeDetailChange(index, "active", e.target.value == "true" ? true : false, false)}
                                                        >
                                                            <option value={true}>Yes</option>
                                                            <option value={false}>No</option>
                                                        </select>
                                                    </Col>
                                                    <Col xxl={1} md={1}>
                                                        {item.id === 0 &&
                                                        <button type="button" className="btn btn-danger btn-sm me-1 mt-1" title="Remove"
                                                            onClick={() => onTestingScopeDetailRemove(index)}
                                                        >
                                                            <i className="ri-delete-bin-2-line" />
                                                        </button>
                                                        }
                                                    </Col>
                                                </Row>
                                            );
                                        })}
                                    </Row>
                                </CardBody>
                            </Card>
                            }
                            {fboState && fboState.testingScope.id > 0 &&
                                <ServiceDocumentGrid
                                    serviceId={-9}
                                    refId={fboState.testingScope.id}
                                    refName={"TESTING_SCOPE"}
                                    parentId={fboState.testingScope.id}
                                    parentName={"TESTING_SCOPE"}
                                    isProduct={false}
                                    collapsed={"false"}
                                    canAddDocument={true}
                                    canDeleteDocument={true}
                                    canAddCertificate={false}
                                    canDeleteCertificate={false}
                                    onChange={onChangeDocument}
                                />
                            }
                        </Col>
                        
                        {fboState && fboState.testingScope.id > 0 &&
                        <Col lg={3}>
                            <ActionList id={fboState.testingScope.id} name={"TESTING_SCOPE"} refreshAction={refreshAction} />
                        </Col>
                        }
                    </Row>
                </Container>
            </div>
        </Fragment>
    );
}

export default IneTestingScopeEditScreen;